import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
} from "@mzc-pdc/ui";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { IconCancel, IconClose, IconErrorCircle } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@components_v2/autocomplete";
import AutocompleteSmallTableList from "@features/autocomplete-table/autocomplete-small-table-list";
import SkeletonAssetsTable from "@features/autocomplete-table/autocomplete-table-skeleton/autocomplete-table-popper-skeleton-assets";
import SkeletonCollectionsTable from "@features/autocomplete-table/autocomplete-table-skeleton/autocomplete-table-popper-skeleton-collections";
import SkeletonContentsTable from "@features/autocomplete-table/autocomplete-table-skeleton/autocomplete-table-popper-skeleton-contents";
import {
    getTableValueByAssetColumn,
    getTableValueByCollectionColumn,
    getTableValueByContentColumn,
} from "@features/autocomplete-table/autocomplete-table-columns-component/autocomplete-table-columns-component";
import { updateCustomContentActions } from "@modules/custom-content";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { setNotification } from "@modules/notification";
import { capitalize } from "@utils/convert-string";
import useCustomContent from "@hooks/useCustomContent";
import SkeletonMixedTable from "@features/autocomplete-table/autocomplete-table-skeleton/autocomplete-table-popper-skeleton-mixed";
import AutocompleteFixedTable from "@features/autocomplete-table/autocomplete-fixed-table";
import { TABLE_LIST_TYPE } from "@constants";
import DialogCreateAsset from "@routes/asset/dialog-create";

const DialogAddCustomContentResourcesV2 = ({
    resource,
    contentId,
    maxLimit,
    onClose,
    hideCheckbox,
    onSubmitCallback,
}) => {
    const { t } = useTranslation();
    const { pending, getFilteredResults, viewMore, onResourceFetch, resetParams } = useCustomContent({
        pageType: "detail",
    });
    const MAX_RESOURCES = resource.type === "PREVIEW" ? 1 : maxLimit;
    const dispatch = useDispatch();
    const { getResourceTableColumns, getResourceSmallTableColumns, getSubNameIds } = useCustomContent();
    const [selectedResources, setSelectedResources] = useState([]);

    const [actionPending, setActionPending] = useState(false);

    const titleType = useMemo(() => {
        switch (resource.type) {
            case "ASSET":
                return "assets";
            case "CONTENT":
                return "contents";
            case "COLLECTION":
                return "collections";
            case "PREVIEW":
                return "preview image";
            default:
                return "";
        }
    }, [resource]);

    const tableListColumns = useMemo(() => {
        return getResourceSmallTableColumns(resource.detailType);
    }, [resource]);

    const tableColumns = useMemo(() => {
        return getResourceTableColumns(resource.detailType);
    }, [resource]);

    const onHandleFetchResources = async (searchParams) => {
        await onResourceFetch({ detailType: resource.detailType, ...searchParams });
    };

    const onHandleResourceSearchData = async (searchParams) => {
        resetParams();
        await onResourceFetch({ detailType: resource.detailType, ...searchParams });
    };

    const onHandleSubmitResources = async () => {
        try {
            setActionPending(true);
            const resourceId = resource.id;
            const ids = selectedResources?.map((v) => v.id);

            if (resource.type !== "PREVIEW") {
                const params = {
                    key: "fields",
                    value: [
                        { id: resource.id, value: Array.from(new Set(ids.concat(resource.originalResources ?? []))) },
                    ],
                };

                await dispatch(updateCustomContentActions({ contentId, params }));
                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(
                            `common::msg::{{resourceName}} '{{name}}' has been added.`,
                            `{{resourceName}} '{{name}}' has been added.`,
                            {
                                resourceName: resource.name,
                                name: selectedResources?.[0].id,
                            },
                        ),
                    }),
                );
                setActionPending(false);
            }
            onClose();
            await onSubmitCallback(resourceId, ids);
        } catch (e) {
            console.log(e);
            dispatch(
                setNotification({
                    type: "error",
                    contents: e.response
                        ? `[Error:${e.response.status}] => ${e.response?.data?.message}`
                        : `Internal Error Occured`,
                }),
            );
            setActionPending(false);
        }
    };

    const onHandleSelectResources = async (checkedList) => {
        setSelectedResources(checkedList);
    };

    const onHandleGetValueByColumns = (id, option) => {
        switch (resource.type) {
            case "ASSET":
            case "PREVIEW":
                return getTableValueByAssetColumn(id, option);
            case "CONTENT":
                return getTableValueByContentColumn(id, option);
            case "COLLECTION":
                return getTableValueByCollectionColumn(id, option);
            default:
                return null;
        }
    };

    const onHandleSkeleton = useMemo(() => {
        switch (resource.type) {
            case "ASSET":
                if (resource?.detailType === "ASSETS")
                    return <SkeletonMixedTable columns={tableColumns.filter((item) => item.useInAutocomplete)} />;
                return <SkeletonAssetsTable columns={tableColumns.filter((item) => item.useInAutocomplete)} />;
            case "PREVIEW":
                return <SkeletonAssetsTable columns={tableColumns.filter((item) => item.useInAutocomplete)} />;
            case "CONTENT":
                return <SkeletonContentsTable columns={tableColumns.filter((item) => item.useInAutocomplete)} />;
            case "COLLECTION":
                return <SkeletonCollectionsTable columns={tableColumns.filter((item) => item.useInAutocomplete)} />;
            default:
                return null;
        }
    }, [tableColumns, resource]);

    const resourceDomain = useMemo(() => {
        switch (resource.type) {
            case "ASSET":
                return TABLE_LIST_TYPE.ASSET;
            case "CONTENT":
                return TABLE_LIST_TYPE.CUSTOM_CONTENT;
            case "COLLECTION":
                return TABLE_LIST_TYPE.COLLECTION;
            default:
                return null;
        }
    });

    const onHandleDeleteResources = (id) => {
        const newResources = selectedResources.filter((el) => el.id !== id);
        setSelectedResources(newResources);
    };

    return (
        <Dialog
            onClose={() => onClose()}
            open={true}
            sx={{
                ".MuiDialog-container >.MuiPaper-root": {
                    minWidth: "1280px",
                },
                ".MuiDialogContent-root": {
                    height: "808px",
                    scrollbarGutter: "unset",
                },
            }}
        >
            <DialogTitle>
                <Grid item>
                    <Typography variant="h3">
                        {t(`common::label::Add {{name}}`, `Add {{name}}`, {
                            name: t(`common::label::${titleType}`, titleType),
                        })}
                    </Typography>
                </Grid>
                <TooltipButton
                    circled
                    title={t(`common::label::Close`)}
                    onClickButton={onClose}
                    buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                />
            </DialogTitle>
            <DialogContent>
                <Grid
                    container
                    flexDirection={"row"}
                    width={"100%"}
                    height={"100%"}
                    borderTop={`1px solid ${theme.palette.greySecondary[100]}`}
                    gap={2.5}
                    pt={2.5}
                >
                    <Grid item flex={1}>
                        {tableColumns && (
                            <AutocompleteFixedTable
                                domain={resourceDomain}
                                type={"table"}
                                key={"autocomplete-add-custom-content-resource-table"}
                                placement={"bottom-end"}
                                placeholder={t(`common::msg::Please search by asset name.`)}
                                onSubmit={onHandleSelectResources}
                                getValueByColumn={onHandleGetValueByColumns}
                                onDebounceSearch={(search) => onHandleResourceSearchData(search)}
                                onFetchData={(search) => onHandleFetchResources(search)}
                                columns={tableColumns}
                                hideCheckbox={hideCheckbox}
                                skeletonColumns={onHandleSkeleton}
                                viewMore={viewMore}
                                options={getFilteredResults({ detailType: resource.detailType })}
                                isPending={pending}
                                selectedItems={selectedResources}
                            />
                        )}
                    </Grid>
                    <Divider orientation={"vertical"} flexItem />
                    <Grid
                        item
                        width={420}
                        sx={{ display: `flex`, flexDirection: `column`, overflow: `hidden`, gap: 2 }}
                    >
                        <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                            <Typography variant={"body1"} color={theme.palette.secondary[800]}>
                                {t(`common::label::Selected {{referenceType}}s`, `Selected`, {
                                    referenceType: t(`common::label::${titleType}`, titleType),
                                })}{" "}
                                {resource.type !== "PREVIEW" && MAX_RESOURCES > 0 && (
                                    <>
                                        (
                                        <Typography
                                            variant={"body1"}
                                            color={
                                                selectedResources.length > MAX_RESOURCES
                                                    ? theme.palette.error[400]
                                                    : theme.palette.primary[400]
                                            }
                                        >
                                            {selectedResources.length}
                                        </Typography>
                                        /{MAX_RESOURCES})
                                    </>
                                )}
                            </Typography>
                            {selectedResources.length > MAX_RESOURCES && (
                                <IconErrorCircle size={16} htmlColor={theme.palette.error.main} />
                            )}
                        </Stack>

                        <Grid item flex={1}>
                            <AutocompleteSmallTableList
                                type={resource.type === "PREVIEW" ? "asset" : resource.type?.toLowerCase()}
                                data={selectedResources}
                                columns={tableListColumns}
                                subNameIds={getSubNameIds(resource.type)}
                                deleteButton={
                                    <Tooltip title={t(`common::label::Delete`)}>
                                        <IconButton
                                            size="medium"
                                            color={"inherit"}
                                            circled
                                            onClick={(e) => {
                                                onHandleDeleteResources(e.target.closest("tr").dataset.id);
                                            }}
                                        >
                                            <IconCancel size={12} htmlColor={theme.palette.grey[700]} />
                                        </IconButton>
                                    </Tooltip>
                                }
                                noDataText={t(
                                    `common::msg::Select {{resourceType}} through search to put them in your custom content.`,
                                    `Select {{resourceType}} through search to put them in your custom content.`,
                                    {
                                        resourceType: t(`common::label::${titleType}`, titleType),
                                    },
                                )}
                                divider={true}
                                gridStyle={{ maxHeight: "670px" }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ borderTop: `1px solid ${theme.palette.greySecondary[100]}`, p: 3 }}>
                <Button
                    color="secondary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    variant="text"
                >
                    {t(`common::label::${"Cancel"}`)}
                </Button>
                <Button
                    color="primary"
                    onClick={() => onHandleSubmitResources()}
                    disabled={
                        selectedResources?.length === 0 || actionPending || MAX_RESOURCES < selectedResources?.length
                    }
                    loading={actionPending}
                    variant="contained"
                >
                    {t(`common::label::${"Add"}`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DialogAddCustomContentResourcesV2;
